import React, { useState, useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { HiBookmark, HiDocumentText, HiCircleStack } from 'react-icons/hi2';
import SavedPaperCard from './SavedPaperCard';
import SavedPackageCard from './SavedPackageCard';
import { CartService } from '../../service/manage/exam_subscription/cart.service';
import { useCart } from '../../context/cart/CartContext';
import { sweetSuccessAlert, sweetErrorAlert } from '../../components/common/SweetAlert';
import swal from 'sweetalert';
import './SavedForLater.css';
import './AccountSectionHeader.css';
import { routes } from '../../helper/routes';

const SavedForLater = () => {
    const history = useHistory();
    const location = useLocation();
    const { fetchCartItemCount } = useCart();

    // Data states
    const [savedPapers, setSavedPapers] = useState([]);
    const [savedPackages, setSavedPackages] = useState([]);
    const [userType] = useState(sessionStorage.getItem('userType'));

    // UI states
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showRemoveDialog, setShowRemoveDialog] = useState(false);
    const [itemToRemove, setItemToRemove] = useState(null);
    const [isAddingToCart, setIsAddingToCart] = useState(false);


    // refresh saved items
    const loadData = async () => {
        setLoading(true);
        setError(null);
        try {
            const user_id = sessionStorage.getItem('manage_user_id');
            const res = await CartService.getSavedItemsForUser(user_id);
            if (res.success) {
                const papers = res.data.assessment_items || [];
                const packages = res.data.credit_package_items || [];

                setSavedPapers(papers);
                setSavedPackages(packages);
            } else {
                setError(res.message || 'Failed to load saved items.');
            }
        } catch (err) {
            setError(err.message || 'Failed to load saved items.');
        } finally {
            setLoading(false);
        }
    };

    // Load saved data on mount
    useEffect(() => {
        loadData();
    }, []);

    // Remove handlers
    const confirmRemoveItem = (itemId, type = 'paper') => {
        setItemToRemove({ id: itemId, type });
        setShowRemoveDialog(true);
    };

    const handleRemoveItem = async () => {
        if (!itemToRemove) return;
        try {
            const { id: itemId } = itemToRemove;
            const res = await CartService.removeSavedItem(itemId);
            if (res.success) {
                await loadData();
                sweetSuccessAlert('Removed!', 'Item removed from saved list successfully.');
            } else {
                sweetErrorAlert('', res.message || 'Failed to remove saved item.');
            }
        } catch (err) {
            console.error('Error removing saved item:', err);
            sweetErrorAlert('', 'Failed to remove item. Please try again.');
        } finally {
            setShowRemoveDialog(false);
            setItemToRemove(null);
        }
    };

    const cancelRemove = () => {
        setShowRemoveDialog(false);
        setItemToRemove(null);
    };

    // Add paper to cart
    const handleAddPaperToCart = async (paper) => {
        if (isAddingToCart) return;

        const confirmed = await swal({
            title: "",
            text: "Add this paper to your cart?",
            icon: "info",
            buttons: {
                cancel: "Cancel",
                confirm: {
                    text: "Yes, Add to Cart",
                    value: true,
                }
            },
            dangerMode: false
        });

        if (!confirmed) {
            return;
        }

        setIsAddingToCart(true);

        try {
            const created_by = sessionStorage.getItem("manage_user_id");
            const cartItem = {
                user_id: paper.user_id,
                product_type: "assessment",
                product_id: paper.product_id,
                quantity: 1,
                created_by: created_by
            };

            const response = await CartService.addToCart(JSON.stringify(cartItem));

            if (response.success) {
                setIsAddingToCart(false);
                fetchCartItemCount();
                const navigateToCart = await swal({
                    title: "Success!",
                    text: "Added to cart successfully",
                    icon: "success",
                    buttons: {
                        cancel: {
                            text: "OK",
                            value: false,
                        },
                        confirm: {
                            text: "Go to Cart",
                            value: true,
                        }
                    },
                    dangerMode: false
                });

                if (navigateToCart) {
                    history.push('/my-cart');
                }
            } else {
                setIsAddingToCart(false);
                sweetErrorAlert("", response.message || "Failed to add to cart");
            }
        } catch (error) {
            setIsAddingToCart(false);
            sweetErrorAlert("", "Failed to add to cart");
            console.error("Add to cart error:", error);
        }
    };

    // Add package to cart
    const handleAddPackageToCart = async (pkg) => {
        if (isAddingToCart) return;

        const confirmed = await swal({
            title: "",
            text: "Add this package to your cart?",
            icon: "info",
            buttons: {
                cancel: "Cancel",
                confirm: {
                    text: "Yes, Add to Cart",
                    value: true,
                }
            },
            dangerMode: false
        });

        if (!confirmed) {
            return;
        }

        setIsAddingToCart(true);

        try {
            const created_by = sessionStorage.getItem("manage_user_id");
            const cartItem = {
                user_id: pkg.user_id,
                product_type: "credit_package",
                product_id: pkg.product_id,
                quantity: 1, 
                created_by: created_by,
            };

            const response = await CartService.addToCart(JSON.stringify(cartItem));

            if (response.success) {
                setIsAddingToCart(false);
                fetchCartItemCount();
                const navigateToCart = await swal({
                    title: "Success!",
                    text: "Added to cart successfully",
                    icon: "success",
                    buttons: {
                        cancel: {
                            text: "OK",
                            value: false,
                        },
                        confirm: {
                            text: "Go to Cart",
                            value: true,
                        }
                    },
                    dangerMode: false
                });

                if (navigateToCart) {
                    history.push('/my-cart');
                }
            } else {
                setIsAddingToCart(false);
                sweetErrorAlert("", response.message || "Failed to add to cart");
            }
        } catch (error) {
            setIsAddingToCart(false);
            sweetErrorAlert("", "Failed to add to cart");
            console.error("Add to cart error:", error);
        }
    };

    // No move-to-cart functionality in this version

    const isEmpty = savedPapers.length === 0 && savedPackages.length === 0;

    return (
        <div className="sfl-page">
            <div className="account-section-page-header">
                <div className="account-section-page-header-left">
                    <h1 className="account-section-page-title">Saved Items</h1>
                    <p className="account-section-page-subtitle">
                        Keep products aside for later and move them back into your cart when you are ready.
                    </p>
                </div>
            </div>

            <div className="cart-page-tabs-wrap">
                <div className="paper-tabs" role="tablist" aria-label="Cart navigation">
                    <button
                        type="button"
                        className={`MuiTab-root ${location.pathname === routes.my_cart ? 'Mui-selected' : ''}`}
                        onClick={() => history.push(routes.my_cart)}
                    >
                        My Cart
                    </button>
                    <button
                        type="button"
                        className={`MuiTab-root ${location.pathname === routes.saved_items ? 'Mui-selected' : ''}`}
                        onClick={() => history.push(routes.saved_items)}
                    >
                        Saved Items
                    </button>
                </div>
            </div>

            {/* Error Message */}
            {error && (
                <div className="sfl-error-message">
                    <p>{error}</p>
                </div>
            )}

            {/* Remove Confirmation Dialog */}
            {showRemoveDialog && (
                <div className="sfl-confirm-overlay">
                    <div className="sfl-confirm-dialog">
                        <h3>Remove Item</h3>
                        <p>Are you sure you want to remove this item from your saved list?</p>
                        <div className="sfl-confirm-actions">
                            <button onClick={cancelRemove} className="sfl-confirm-cancel">
                                Cancel
                            </button>
                            <button onClick={handleRemoveItem} className="sfl-confirm-remove">
                                Remove
                            </button>
                        </div>
                    </div>
                </div>
            )}


            {/* Loading State */}
            {loading ? (
                <div className="sfl-loading-state">
                    <p>Loading saved items...</p>
                </div>
            ) : isEmpty ? (
                <div className="sfl-empty-state">
                    <HiBookmark className="sfl-empty-icon" />
                    <p className="sfl-empty-text">No items saved for later.</p>
                    <p className="sfl-empty-sub">Save exams and credit packages here to purchase later from your cart.</p>
                </div>
            ) : (
                <div className="sfl-main-content container-fluid px-0">
                    {/* Saved Papers Section */}
                    {savedPapers.length > 0 && (
                        <div className="sfl-section row g-0">
                            <div className="col-12">
                            <h2 className="sfl-section-title"><HiDocumentText className="sfl-section-icon" /> Saved Papers</h2>
                            <div className="sfl-items-list">
                                {savedPapers.map((paper) => (
                                    <SavedPaperCard
                                        key={paper.saved_later_item_id}
                                        paper={{
                                            id: paper.saved_later_item_id, 
                                            title: paper.name,
                                            price: paper.amount,
                                            credits: paper.quantity || 0,
                                            quantity: paper.quantity,
                                            savedAt: paper.saved_at,
                                            currencySymbol: paper.currency_symbol,
                                            user_code: paper.user_code,
                                        }}
                                        userType={userType}
                                        onRemove={() => confirmRemoveItem(paper.saved_later_item_id, 'paper')}
                                        onAddToCart={() => handleAddPaperToCart(paper)}
                                    />
                                ))}
                            </div>
                            </div>
                        </div>
                    )}

                    {/* Saved Packages Section */}
                    {savedPackages.length > 0 && (
                        <div className="sfl-section row g-0">
                            <div className="col-12">
                            <h2 className="sfl-section-title"><HiCircleStack className="sfl-section-icon" /> Saved Packages</h2>
                            <div className="sfl-items-list">
                                {savedPackages.map((pkg) => (
                                    <SavedPackageCard
                                        key={pkg.saved_later_item_id}
                                        pkg={{
                                            id: pkg.saved_later_item_id,
                                            name: pkg.name,
                                            credits: pkg.credits,
                                            price: pkg.total_price,
                                            credits_expiry_duration: pkg.credits_expiry_duration,
                                            credits_expiry_type: pkg.credits_expiry_type,
                                            price_before_discount: parseFloat(pkg.credit_amount_before_discount),
                                            discount: pkg.discount_rate ? `${pkg.discount_rate}%` : null,
                                            quantity: pkg.quantity,
                                            savedAt: pkg.saved_at,
                                            currencySymbol: pkg.currency_symbol,
                                            user_code: pkg.user_code,
                                        }}
                                        userType={userType}
                                        onRemove={() => confirmRemoveItem(pkg.saved_later_item_id, 'package')}
                                        onAddToCart={() => handleAddPackageToCart(pkg)}
                                    />
                                ))}
                            </div>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default SavedForLater;
